import { useEffect, useState } from "react";
import { Button, Card, CardContent, CardHeader, CardTitle, Badge, ConfirmDialog, LoadingState } from "@wa/ui";
import { Plus, Trash2, KeyRound, Fingerprint } from "lucide-react";
import { useT, useI18n } from "../../../i18n/I18nProvider";
import { passkeyApi, isWebAuthnReady, isWebAuthnCancel, type Passkey } from "../../../lib/security";
import { fmt } from "./lib";

// ── Passkeys (WebAuthn / biometrics) ──────────────────────────────────────────
export function PasskeysCard() {
  const t = useT();
  const { locale } = useI18n();
  const [items, setItems] = useState<Passkey[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // The passkey the confirm dialog asks about; null = the dialog is closed.
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const supported = isWebAuthnReady();

  async function load() {
    try {
      const { passkeys } = await passkeyApi.list();
      setItems(passkeys);
    } catch {
      setItems([]);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function add() {
    setAdding(true);
    setError(null);
    try {
      await passkeyApi.register();
      await load();
    } catch (err) {
      if (!isWebAuthnCancel(err)) setError(t("passkey.addFailed"));
    } finally {
      setAdding(false);
    }
  }

  async function remove() {
    const id = confirmId;
    if (!id) return;
    setDeletingId(id);
    try {
      await passkeyApi.remove(id);
      await load();
    } catch {
      setError(t("common.error"));
    } finally {
      setDeletingId(null);
      setConfirmId(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <KeyRound className="size-5 text-primary" />
          <div className="flex-1">
            <CardTitle>{t("passkey.title")}</CardTitle>
            <p className="text-sm text-muted-foreground">{t("passkey.desc")}</p>
          </div>
          {supported && (
            <Button size="sm" variant="outline" onClick={add} loading={adding}>
              <Plus className="mr-1 size-4" />
              {t("passkey.add")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        {!supported ? (
          <p className="text-sm text-muted-foreground">{t("passkey.unsupported")}</p>
        ) : items === null ? (
          <LoadingState variant="inline" label={t("common.loading")} />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("passkey.empty")}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((p) => (
              <li key={p.id} className="flex items-center gap-3 rounded-md border bg-card p-3">
                <Fingerprint className="size-4 shrink-0 text-emerald-600" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="truncate">{p.deviceName || t("security.unknownDevice")}</span>
                    {p.backupEligible && <Badge variant="secondary">{t("passkey.synced")}</Badge>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {t("passkey.addedOn", { date: fmt(p.createdAt, locale) })}
                    {p.lastUsedAt ? ` · ${t("passkey.lastUsed", { date: fmt(p.lastUsedAt, locale) })}` : ""}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setConfirmId(p.id)} loading={deletingId === p.id}>
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <ConfirmDialog
        open={confirmId !== null}
        variant="destructive"
        title={t("passkey.confirmRemove")}
        confirmLabel={t("passkey.remove")}
        cancelLabel={t("common.cancel")}
        loading={deletingId !== null}
        onConfirm={() => void remove()}
        onCancel={() => setConfirmId(null)}
      />
    </Card>
  );
}
